/**
 * Offline Queue - IndexedDB storage for pending workout syncs
 * 
 * When a user finishes a workout but is offline (or save fails),
 * the workout data is queued here and synced on next app load.
 */

const DB_NAME = 'begin-strength-offline';
const DB_VERSION = 1;
const STORE_NAME = 'pending-workouts';

let db = null;

/**
 * Initialize the IndexedDB database
 */
function initDB() {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    if (!window.indexedDB) {
      console.warn('[OfflineQueue] IndexedDB not supported');
      reject(new Error('IndexedDB not supported'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('[OfflineQueue] Failed to open database:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.log('[OfflineQueue] Database opened');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      
      // Create the pending workouts store
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { 
          keyPath: 'id', 
          autoIncrement: true 
        });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('userId', 'userId', { unique: false });
        console.log('[OfflineQueue] Created pending-workouts store');
      }
    };
  });
}

/**
 * Queue a workout for later sync
 * 
 * @param {string} userId - The user's ID
 * @param {object} sessionData - Workout session metadata
 * @param {object} workoutData - Exercise/set data
 * @returns {Promise<number>} - The queued item's ID
 */
export async function queueWorkout(userId, sessionData, workoutData) {
  try {
    const database = await initDB();
    
    return new Promise((resolve, reject) => {
      const transaction = database.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const item = {
        userId,
        sessionData,
        workoutData,
        timestamp: Date.now(),
        attempts: 0
      };
      
      const request = store.add(item);
      
      request.onsuccess = () => {
        console.log('[OfflineQueue] Workout queued with ID:', request.result);
        resolve(request.result);
      };
      
      request.onerror = () => {
        console.error('[OfflineQueue] Failed to queue workout:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('[OfflineQueue] Queue error:', error);
    throw error;
  }
}

/**
 * Get all pending workouts for a user
 * 
 * @param {string} userId - The user's ID
 * @returns {Promise<Array>} - Array of pending workout items
 */
export async function getPendingWorkouts(userId) {
  try {
    const database = await initDB();
    
    return new Promise((resolve, reject) => {
      const transaction = database.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('userId');
      const request = index.getAll(userId);
      
      request.onsuccess = () => {
        const items = request.result || [];
        console.log('[OfflineQueue] Found', items.length, 'pending workouts for user');
        resolve(items);
      };
      
      request.onerror = () => {
        console.error('[OfflineQueue] Failed to get pending workouts:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('[OfflineQueue] Get pending error:', error);
    return [];
  }
}

/**
 * Get count of all pending workouts (for sync indicator)
 * 
 * @returns {Promise<number>} - Number of pending workouts
 */
export async function getPendingCount() {
  try {
    const database = await initDB();
    
    return new Promise((resolve, reject) => {
      const transaction = database.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.count();
      
      request.onsuccess = () => {
        resolve(request.result);
      };
      
      request.onerror = () => {
        console.error('[OfflineQueue] Failed to count pending:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('[OfflineQueue] Count error:', error);
    return 0;
  }
}

/**
 * Remove a workout from the queue (after successful sync)
 * 
 * @param {number} id - The queued item's ID
 * @returns {Promise<void>}
 */
export async function removeFromQueue(id) {
  try {
    const database = await initDB();
    
    return new Promise((resolve, reject) => {
      const transaction = database.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);
      
      request.onsuccess = () => {
        console.log('[OfflineQueue] Removed item:', id);
        resolve();
      };
      
      request.onerror = () => {
        console.error('[OfflineQueue] Failed to remove item:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('[OfflineQueue] Remove error:', error);
    throw error;
  }
}

/**
 * Sync all pending workouts for a user
 * 
 * @param {string} userId - The user's ID
 * @param {function} saveFunction - The function to save workout (from database.js)
 * @returns {Promise<{synced: number, failed: number}>}
 */
export async function syncPendingWorkouts(userId, saveFunction) {
  const pending = await getPendingWorkouts(userId);
  
  if (pending.length === 0) {
    console.log('[OfflineQueue] No pending workouts to sync');
    return { synced: 0, failed: 0 };
  }
  
  console.log('[OfflineQueue] Syncing', pending.length, 'pending workouts...');
  
  let synced = 0;
  let failed = 0;
  
  for (const item of pending) {
    try {
      await saveFunction(userId, item.sessionData, item.workoutData);
      await removeFromQueue(item.id);
      synced++;
      console.log('[OfflineQueue] Synced workout from', new Date(item.timestamp).toLocaleString());
    } catch (error) {
      console.error('[OfflineQueue] Failed to sync workout:', error);
      failed++;
      // Don't remove from queue - will retry next time
    }
  }
  
  console.log('[OfflineQueue] Sync complete:', { synced, failed });
  return { synced, failed };
}

/**
 * Clear all pending workouts (for testing/reset)
 */
export async function clearQueue() {
  try {
    const database = await initDB();
    
    return new Promise((resolve, reject) => {
      const transaction = database.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();
      
      request.onsuccess = () => {
        console.log('[OfflineQueue] Queue cleared');
        resolve();
      };
      
      request.onerror = () => {
        console.error('[OfflineQueue] Failed to clear queue:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('[OfflineQueue] Clear error:', error);
    throw error;
  }
}




